// Sentry init for the renderer. Two runtimes share this module:
// - Electron renderer → @sentry/electron/renderer (forwards events
//   through the main process for retry + native crash plumbing).
// - Web browser       → @sentry/react (events go straight to Sentry).
//
// Both expose the same `Sentry.*` API surface (`@sentry/electron/renderer`
// re-exports the browser SDK), so identify/reset/captureException stay
// runtime-agnostic.

interface SentryEventLike {
  message?: string;
  exception?: {
    values?: Array<{ value?: string; mechanism?: { type?: string } }>;
  };
}

export function shouldDropSentryEvent(event: SentryEventLike): boolean {
  const exc = event.exception?.values?.[0];
  const msg = exc?.value ?? event.message ?? "";

  // Sentry SDK self-test event fired once on init.
  if (
    /Simulated Exception/i.test(msg) ||
    exc?.mechanism?.type === "simulated"
  ) {
    return true;
  }

  // Replicache/Zero IndexedDB cleanup logs as fatal but the message
  // itself says "non-fatal. The app will continue running in memory
  // until reload."
  if (/Expected IndexedDB not found/i.test(msg) && /non-fatal/i.test(msg)) {
    return true;
  }

  // ResizeObserver loop notifications. Fired by some Radix/Tiptap
  // components when measuring layout — never actionable.
  if (/ResizeObserver loop/i.test(msg)) {
    return true;
  }

  // AbortError from fetches cancelled by component unmounts or
  // navigation. The corresponding HTTP request is gone; the abort is
  // expected control flow, not a bug.
  if (/AbortError|The operation was aborted|signal is aborted/i.test(msg)) {
    return true;
  }

  // Browser extension errors leak through window.onerror with
  // chrome-extension:// frames. Not our code.
  if (/Extension context invalidated|chrome-extension:\/\//i.test(msg)) {
    return true;
  }

  return false;
}

function resolveEnvironment(): string {
  if (import.meta.env.MODE === "development") return "development";
  const buildTarget = import.meta.env.VITE_BUILD_TARGET as string | undefined;
  return buildTarget === "production" ? "production" : "staging";
}

interface SentryRuntimeApi {
  init: (opts: Record<string, unknown>) => void;
  setUser: (
    user: { id: string; email?: string; username?: string } | null,
  ) => void;
  setTag: (key: string, value: string) => void;
  captureException: (e: unknown, hint?: unknown) => void;
  addBreadcrumb: (breadcrumb: {
    category?: string;
    message?: string;
    level?: "info" | "warning" | "error";
    data?: Record<string, unknown>;
  }) => void;
}

let runtime: SentryRuntimeApi | null = null;
let initStarted = false;

export function initSentry(): void {
  if (runtime || initStarted) return;
  initStarted = true;

  // Don't ship dev events. HMR/Vite-deps churn (useContext null in
  // useAuth, getSnapshot null in useSyncExternalStore, listenersRef
  // delete from /@react-refresh, "Failed to fetch dynamically imported
  // module: localhost:1420/...") floods the inbox with React-internal
  // refresh artifacts that aren't real bugs. Devs still see exceptions
  // in console + devtools.
  if (import.meta.env.MODE === "development") return;

  const isElectron = Boolean(
    (window as unknown as { __electron?: boolean }).__electron,
  );

  const dsn = (
    import.meta.env.VITE_SENTRY_DSN_ELECTRON as string | undefined
  )?.trim();
  if (!dsn) return;

  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? null;

  const importPromise = isElectron
    ? import("@sentry/electron/renderer")
    : import("@sentry/react");

  importPromise
    .then((mod) => {
      const sdk = mod as unknown as SentryRuntimeApi;
      sdk.init({
        dsn,
        environment: resolveEnvironment(),
        ...(release ? { release } : {}),
        // 10% sampling in deployed builds; full sampling in dev so local
        // perf work shows up.
        tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
        beforeSend(event: SentryEventLike) {
          return shouldDropSentryEvent(event) ? null : event;
        },
      });
      sdk.setTag("runtime", isElectron ? "desktop" : "web");
      runtime = sdk;
      (
        window as unknown as {
          __sentryCaptureException?: (e: unknown, hint?: unknown) => void;
        }
      ).__sentryCaptureException = (e, hint) => sdk.captureException(e, hint);
    })
    .catch((err) => {
      console.error("[sentry] renderer init failed:", err);
    });
}

export function identifySentryUser(opts: {
  id: string;
  email?: string | null;
  name?: string | null;
}): void {
  if (!runtime) return;
  runtime.setUser({
    id: opts.id,
    ...(opts.email ? { email: opts.email } : {}),
    ...(opts.name ? { username: opts.name } : {}),
  });
}

export function resetSentryUser(): void {
  if (!runtime) return;
  runtime.setUser(null);
}

/**
 * Send an exception to Sentry. Safe to call before init completes — the
 * call no-ops if the runtime hasn't loaded yet (and we don't want
 * mutation-error reporting to fail closed). `context` becomes a tag so
 * events from the send pipeline are easy to slice from generic crashes.
 */
export function captureSentryException(
  err: unknown,
  context?: { tag?: string; extra?: Record<string, unknown> },
): void {
  if (!runtime) return;
  const hint = context
    ? {
        tags: context.tag ? { context: context.tag } : undefined,
        extra: context.extra,
      }
    : undefined;
  runtime.captureException(err, hint);
}

/**
 * Light wrapper around `Sentry.addBreadcrumb`. Breadcrumbs ride along
 * the next captured exception, so dropping them at key points in
 * notification / badge / IPC code paths lets us reconstruct WHY a
 * report-from-prod failed (e.g. "no events delivered", "permission
 * denied", "electron API missing") without forcing the user to open
 * DevTools.
 */
export function addSentryBreadcrumb(crumb: {
  category: string;
  message: string;
  level?: "info" | "warning" | "error";
  data?: Record<string, unknown>;
}): void {
  if (!runtime) return;
  try {
    runtime.addBreadcrumb({
      category: crumb.category,
      message: crumb.message,
      level: crumb.level ?? "info",
      data: crumb.data,
    });
  } catch {
    // Sentry SDK can throw on init races — never let breadcrumb noise
    // break the calling code.
  }
}
